import { useEffect, useState } from "react";
import { userState } from "../../state/UserState";
import { PhonePreview } from "../../components/PhonePreview";
import { scheduleNotification, sendVenueFlashMessage } from "../../api/new-api";
import { ErrorAlert } from "../../components/alerts";
import { PEProgressBar } from "../../components/linear-progress";
import { SCHEDULE_NOTIFICATIONS } from "./player-communications-page";
import { useColors } from "../../provider/colors-provider";
import { Alert, Snackbar } from "@mui/material";
import CustomInput from "../../components/CustomInput";
import CustomTextarea from "../../components/CustomTextarea";

type Props = {
  scheduleType: string;
  notificationScheduleTime: Date;
  refetch: () => void;
};

const VenueNotification = ({
  scheduleType,
  notificationScheduleTime,
  refetch,
}: Props) => {
  const { primary } = useColors();
  const [notification, setNotification] = useState<any>({
    title: null,
    message: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const validate = () => {
    const { title, message } = notification;

    if (title === "" || message === "" || !title || !message) {
      setError("Title and message are required");
      return false;
    }
    return true;
  };

  const sendToVenue = async () => {
    setLoading(true);
    setError("");
    try {
      if (scheduleType === SCHEDULE_NOTIFICATIONS.NOW) {
        await sendVenueFlashMessage(notification);
        setNotification({
          title: null,
          message: null,
        });
        setSuccessMessage("Success: Notification sent to venue.");
        setSuccessOpen(true);
      }

      if (scheduleType === SCHEDULE_NOTIFICATIONS.FUTURE) {
        const { title, message } = notification;
        const scheduledTime = notificationScheduleTime.getTime();
        const currentTime = new Date().getTime();

        if (scheduledTime < currentTime) {
          setError("Scheduled time cannot be in the past");
          return;
        }

        await scheduleNotification({
          scheduleType: "OnceInFuture",
          notificationType: "AllVenue",
          notificationTitle: title,
          notificationMessage: message,
          notificationScheduleTime: scheduledTime,
        });
        setNotification({ title: null, message: null });
        setSuccessMessage("Success: Venue notification scheduled.");
        setSuccessOpen(true);
      }

      await refetch();
    } catch (error) {
      setError("Error sending venue flash message");
      console.error("Error sending venue flash message", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (validate()) {
      sendToVenue();
    }
  };

  useEffect(() => {
    setError("");
  }, [notification]);

  return (
    <div className="bg-white flex  items-center justify-center text-center mb-14">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          Venue Notification
        </h3>
        <div className="mt-2 max-w-xl text-sm text-gray-500">
          <p>
            Send a notification to whole venue. You can edit title and message
            below:
          </p>
        </div>
        <div>
          <form
            className="mt-5 sm:flex sm:items-center"
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <div className="flex flex-col gap-2 w-full sm:max-w-xl">
              <CustomInput
                value={notification.title ?? ""}
                onChange={(e) =>
                  setNotification({
                    ...notification,
                    title: e.target.value,
                  })
                }
                id="title"
                name="title"
                type="title"
                placeholder="Notification Title"
              />

              <CustomTextarea
                value={notification.message ?? ""}
                onChange={(e) =>
                  setNotification({
                    ...notification,
                    message: e.target.value,
                  })
                }
                id="message"
                name="message"
                placeholder="Notification Message"
                type={""}
              />
            </div>
          </form>

          <div className="mt-5">
            {error && <ErrorAlert message={error} />}
            {loading ? (
              <PEProgressBar />
            ) : (
              <button
                onClick={handleSubmit}
                type="button"
                style={{
                  backgroundColor: primary,
                }}
                className="w-full rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
              >
                Send to Venue
              </button>
            )}
          </div>
        </div>
        <div className="mt-5">
          <PhonePreview
            screenType="NOTIFICATION_PREVIEW"
            notificationTitle={notification.title}
            notificationMessage={notification.message}
            userState={userState}
          />
        </div>
        <Snackbar
          open={successOpen}
          autoHideDuration={3000}
          onClose={() => setSuccessOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSuccessOpen(false)}
            severity="success"
            variant="filled"
          >
            {successMessage}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default VenueNotification;
